import type { CSSProperties } from 'react'
import type { FlightDirection } from './FlightDirectionCard'
import type { FlightGroup } from './flightGroups'
import cn from 'classnames'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { hasFlightAirlineServicesContent } from '../../utils/flightAirlineServices'
import { hasMixedFlightStops } from '../../utils/flightDetails'
import { hasFlightLuggageContent } from '../../utils/flightLuggage'
import styles from './FlightDetailsSection.module.css'
import { FlightDirectionCard } from './FlightDirectionCard'

interface FlightGroupCardsProps {
  group: FlightGroup
  activeDirection: FlightDirection
  isPastBooking: boolean
  isCancelled: boolean
}

interface FlightCardsStyle extends CSSProperties {
  '--flight-card-section-count': number
}

const ALWAYS_VISIBLE_CARD_SECTIONS = 2

function getFlightCardSectionCount(showLuggageSection: boolean, showAirlineServices: boolean): number {
  return ALWAYS_VISIBLE_CARD_SECTIONS + Number(showLuggageSection) + Number(showAirlineServices)
}

export function FlightGroupCards({ group, activeDirection, isPastBooking, isCancelled }: FlightGroupCardsProps) {
  const airlineBaggageFaqUrl = useConfiguration('links.airlineBaggageFaq')
  const allLegs = [...group.outbound, ...group.inbound]
  const directions: { direction: FlightDirection, legs: FlightGroup[FlightDirection] | null }[] = [
    { direction: 'outbound', legs: group.outbound.length ? group.outbound : null },
    { direction: 'inbound', legs: group.inbound.length ? group.inbound : null },
  ]
  const showDirectLabel = hasMixedFlightStops(directions.map(({ legs }) => legs))
  const showLuggageSection = hasFlightLuggageContent(allLegs)
  const showAirlineServices = !isPastBooking
    && !isCancelled
    && hasFlightAirlineServicesContent(allLegs, airlineBaggageFaqUrl)
  const flightCardSectionCount = getFlightCardSectionCount(showLuggageSection, showAirlineServices)
  const cardsStyle: FlightCardsStyle = { '--flight-card-section-count': flightCardSectionCount }

  return (
    <div
      className={styles.cards}
      style={cardsStyle}
    >
      {directions.map(({ direction, legs }) => (
        <div
          key={direction}
          className={cn(styles.card, {
            [styles.cardHidden]: activeDirection !== direction,
          })}
        >
          <FlightDirectionCard
            direction={direction}
            legs={legs}
            isCancelled={isCancelled}
            showDirectLabel={showDirectLabel}
            showLuggageSection={showLuggageSection}
            showAirlineServices={showAirlineServices}
          />
        </div>
      ))}
    </div>
  )
}
